import { useEffect } from "react";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";

interface UseDirtyStateOptions {
	hasUnsavedChanges: boolean;
}

export function useDirtyState({ hasUnsavedChanges }: UseDirtyStateOptions) {
	const { setHasUnsavedChanges } = useNavigationGuard();

	// Sync unsaved changes state with navigation guard
	useEffect(() => {
		setHasUnsavedChanges(hasUnsavedChanges);

		// Clean up when component unmounts
		return () => {
			setHasUnsavedChanges(false);
		};
	}, [hasUnsavedChanges, setHasUnsavedChanges]);

	// Warn user before leaving page with unsaved changes (browser navigation)
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				e.preventDefault();
				// Modern browsers require returnValue to be set
				e.returnValue = "";
				return "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [hasUnsavedChanges]);

	// Block browser back/forward navigation when there are unsaved changes
	useEffect(() => {
		if (!hasUnsavedChanges) return;

		// Push a dummy state to enable popstate detection
		window.history.pushState(null, "", window.location.href);

		const handlePopState = () => {
			if (hasUnsavedChanges) {
				// Show confirmation dialog
				const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.");

				if (confirmLeave) {
					// Allow navigation by going back again
					window.history.back();
				} else {
					// Stay on page - push state again to re-enable detection
					window.history.pushState(null, "", window.location.href);
				}
			}
		};

		window.addEventListener("popstate", handlePopState);
		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [hasUnsavedChanges]);
}
